import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Header from "@/components/Header";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Mail, Search, RefreshCw, Download, AlertTriangle, ShieldCheck, X, ChevronLeft, ChevronRight } from "lucide-react";

const PER = 50;
const STATUS = [["all", "Everything"], ["sent", "Delivered"], ["failed", "Failed"]];

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const Pill = ({ ok }) => (
  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide"
    style={ok ? { background: "rgba(0,134,58,0.12)", color: "#00863A" } : { background: "rgba(185,28,28,0.12)", color: "#B91C1C" }}>
    {ok ? "delivered" : "failed"}
  </span>
);

export default function AdminEmailLog() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [health, setHealth] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [kind, setKind] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/email/activity", { params: { q, status, kind, start, end, limit: PER, skip: page * PER } }),
        api.get("/admin/email/health"),
      ]);
      setD(a.data);
      setHealth(b.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, [q, status, kind, start, end, page]);

  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => { setPage(0); }, [q, status, kind, start, end]);

  const recheck = async () => {
    try {
      const { data } = await api.post("/admin/email/domain-check");
      toast[data.status === "warning" ? "warning" : "success"](data.message, { duration: 8000 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const csvHref = () => {
    const t = localStorage.getItem("cp_token") || "";
    const p = new URLSearchParams({ q, status, kind, start, end, token: t });
    return `${process.env.REACT_APP_BACKEND_URL}/api/admin/email/activity.csv?${p.toString()}`;
  };

  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_super_admin) return <Navigate to="/admin/members" replace />;

  const dw = health?.domain_watch;
  const pages = Math.max(1, Math.ceil((d?.total || 0) / PER));

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10">
        <div className="mb-6 fade-up">
          <div className="pill mb-3"><span className="dot" />Admin · Email activity</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">Email log</h1>
          <p className="text-ink-2 text-sm mt-2 max-w-2xl">
            Every message the system tried to send — sign-in codes, invites, sync requests — with the recipient,
            the member behind the address and the provider's own error when a send fails.
          </p>
        </div>

        {dw && (
          <div className="glass p-3.5 mb-4 flex flex-wrap items-center gap-3" data-testid="email-domain-watch"
            style={{ borderLeft: `3px solid ${dw.status === "warning" ? "#B45309" : "#00863A"}` }}>
            <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0"
              style={{ background: dw.status === "warning" ? "rgba(180,83,9,0.12)" : "rgba(0,134,58,0.10)",
                       color: dw.status === "warning" ? "#B45309" : "#00863A" }}>
              {dw.status === "warning" ? <AlertTriangle size={16} /> : <ShieldCheck size={16} />}
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-[12px] font-bold">
                Sending domain · {dw.domain}
                <span className="text-ink-3 font-semibold"> · checked {fmt(dw.checked_at)}</span>
              </div>
              <div className="text-[12px] text-ink-2" data-testid="email-domain-watch-msg">{dw.message}</div>
            </div>
            <button onClick={recheck} className="btn-secondary" data-testid="email-domain-recheck">
              <RefreshCw size={13} /> Re-check now
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <div className="kpi-tile" data-testid="email-kpi-total"><div className="kpi-label">Attempts</div><div className="kpi-num">{d?.total ?? "—"}</div></div>
          <div className="kpi-tile" data-testid="email-kpi-sent"><div className="kpi-label">Delivered</div><div className="kpi-num" style={{ color: "#00863A" }}>{d?.sent ?? "—"}</div></div>
          <div className="kpi-tile" data-testid="email-kpi-failed"><div className="kpi-label">Failed</div><div className="kpi-num" style={{ color: "#B91C1C" }}>{d?.failed ?? "—"}</div></div>
          <div className="kpi-tile" data-testid="email-kpi-people"><div className="kpi-label">Recipients</div><div className="kpi-num" style={{ color: "#4060FF" }}>{d?.people ?? "—"}</div></div>
        </div>

        <div className="glass p-4 mb-4 flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: "rgba(15,18,38,0.04)" }}>
            <Search size={15} className="text-ink-3" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="email-log-search"
              placeholder="Search a person, address, subject or error…" className="flex-1 bg-transparent outline-none text-sm" />
            {q && <button onClick={() => setQ("")} data-testid="email-log-search-clear"><X size={13} className="text-ink-3" /></button>}
          </div>
          <div className="flex items-center gap-1 p-1 rounded-xl glass">
            {STATUS.map(([k, label]) => (
              <button key={k} onClick={() => setStatus(k)} data-testid={`email-log-status-${k}`}
                className={`px-3 py-1.5 rounded-lg text-[12px] font-bold transition-colors ${status === k ? "bg-white shadow-sm" : "text-ink-3"}`}>
                {label}
              </button>
            ))}
          </div>
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="input-glass !py-2 text-sm" data-testid="email-log-kind">
            <option value="all">All types</option>
            {(d?.kinds || []).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <input type="date" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} className="input-glass !py-2 text-sm" data-testid="email-log-start" />
          <input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} className="input-glass !py-2 text-sm" data-testid="email-log-end" />
          <button onClick={load} className="btn-secondary" data-testid="email-log-refresh"><RefreshCw size={13} className={busy ? "animate-spin" : ""} /></button>
          <a href={csvHref()} className="btn-secondary" data-testid="email-log-csv"><Download size={13} /> CSV</a>
        </div>

        <div className="glass overflow-hidden mb-6">
          <div className="overflow-auto">
            <table className="w-full text-sm" data-testid="email-log-table">
              <thead>
                <tr className="text-left" style={{ background: "rgba(15,18,38,0.03)" }}>
                  {["When", "Status", "Type", "Member", "Address", "Subject"].map((h) => (
                    <th key={h} className="py-2.5 px-3 text-[10px] font-bold tracking-[0.1em] uppercase text-ink-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(d?.items || []).map((e) => (
                  <tr key={e.id} className="border-t hover:bg-black/[0.015]" style={{ borderColor: "rgba(15,18,38,0.06)" }} data-testid={`email-log-row-${e.id}`}>
                    <td className="py-2 px-3 text-[12px] whitespace-nowrap text-ink-2">{fmt(e.created_at)}</td>
                    <td className="py-2 px-3"><Pill ok={e.ok} /></td>
                    <td className="py-2 px-3 text-[12px] whitespace-nowrap">{e.kind}</td>
                    <td className="py-2 px-3 text-[12px] font-semibold whitespace-nowrap">{e.member || <span className="text-ink-3">not a member</span>}</td>
                    <td className="py-2 px-3 text-[11px] font-mono text-ink-2">{e.to}</td>
                    <td className="py-2 px-3 text-[12px]">
                      {e.subject}
                      {!e.ok && e.error && <div className="text-[11px] mt-0.5" style={{ color: "#B91C1C" }}>{e.error}</div>}
                    </td>
                  </tr>
                ))}
                {!(d?.items || []).length && (
                  <tr><td colSpan={6} className="py-12 text-center text-ink-3 text-sm" data-testid="email-log-empty">
                    {busy ? "Loading…" : "No emails match these filters"}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t" style={{ borderColor: "rgba(15,18,38,0.06)" }}>
              <span className="text-[11px] text-ink-3">Page {page + 1} of {pages} · {d?.total} attempts</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="btn-secondary disabled:opacity-40" data-testid="email-log-prev"><ChevronLeft size={13} /></button>
                <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} className="btn-secondary disabled:opacity-40" data-testid="email-log-next"><ChevronRight size={13} /></button>
              </div>
            </div>
          )}
        </div>

        <div className="glass overflow-hidden">
          <div className="px-4 py-2.5 font-display text-sm font-semibold flex items-center gap-2" style={{ background: "rgba(15,18,38,0.03)" }}>
            <Mail size={14} /> Per member · {(d?.by_member || []).length} recipients
          </div>
          <div className="overflow-auto" style={{ maxHeight: 380 }}>
            <table className="w-full text-sm" data-testid="email-log-by-member">
              <thead className="sticky top-0 bg-white">
                <tr className="text-left" style={{ boxShadow: "inset 0 -1px 0 rgba(15,18,38,0.06)" }}>
                  {["Member", "Address", "Types", "Delivered", "Failed", "Last email"].map((h) => (
                    <th key={h} className="py-2 px-3 text-[10px] font-bold tracking-[0.1em] uppercase text-ink-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(d?.by_member || []).map((p) => (
                  <tr key={p.email} className="border-t hover:bg-black/[0.015]" style={{ borderColor: "rgba(15,18,38,0.06)" }}
                    data-testid={`email-member-row-${p.email}`}>
                    <td className="py-1.5 px-3 text-[12px] font-semibold whitespace-nowrap">{p.member || <span className="text-ink-3">not a member</span>}</td>
                    <td className="py-1.5 px-3 text-[11px] font-mono text-ink-2">{p.email}</td>
                    <td className="py-1.5 px-3 text-[11px] text-ink-3">{(p.kinds || []).join(", ")}</td>
                    <td className="py-1.5 px-3 text-[12px] font-mono font-bold" style={{ color: "#00863A" }}>{p.sent}</td>
                    <td className="py-1.5 px-3 text-[12px] font-mono font-bold" style={{ color: p.failed ? "#B91C1C" : "#B9B3CC" }}>{p.failed}</td>
                    <td className="py-1.5 px-3 text-[12px] text-ink-2 whitespace-nowrap">{fmt(p.last_at)}</td>
                  </tr>
                ))}
                {!(d?.by_member || []).length && <tr><td colSpan={6} className="py-8 text-center text-ink-3 text-sm">Nothing yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
